/**
 * Локальный прогресс. localStorage может отсутствовать или бросать (приватный
 * режим, запрет cookies) — тогда игра работает, просто ничего не помнит.
 */

const BEST_KEY = 'volan:best'
const HISTORY_KEY = 'volan:history'
const OUTBOX_KEY = 'volan:outbox'
const PLAYER_KEY = 'volan:player'
const SKIN_KEY = 'volan:skin'
const MUTED_KEY = 'volan:muted'
const COINS_KEY = 'volan:coins'
/** Хранится только хвост: для «последних партий» хватит. */
export const HISTORY_LIMIT = 50
/**
 * Очередь на сервер. Если ника нет или сети нет неделями, старые партии
 * вытесняются: лидерборду нужны лучшие недавние, а не все.
 */
export const OUTBOX_LIMIT = 50

export interface GameRecord {
  /** uuid партии — по нему сервер отличает повторную отправку от новой партии. */
  id: string
  score: number
  hits: number
  /** Длительность партии, с. */
  duration: number
  /** Время окончания, ISO. */
  playedAt: string
}

/** Анонимный игрок: токен выдаёт сервер один раз, восстановить его нельзя. */
export interface PlayerIdentity {
  id: number
  nickname: string
  token: string
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Квота или запрет хранилища — прогресс не сохранится, но играть можно.
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // См. write.
  }
}

function readJson(key: string): unknown {
  try {
    return JSON.parse(read(key) ?? 'null')
  } catch {
    return null
  }
}

function readRecords(key: string): GameRecord[] {
  const parsed = readJson(key)
  return Array.isArray(parsed) ? parsed.filter(isRecord) : []
}

export function getBestScore(): number {
  const value = Number(read(BEST_KEY))
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
}

export function getHistory(): GameRecord[] {
  return readRecords(HISTORY_KEY)
}

/**
 * Записывает партию в историю и в очередь на сервер; возвращает рекорд после
 * неё, побит ли он и саму запись.
 */
export function recordGame(
  game: { score: number; hits: number; duration: number },
  now = new Date(),
): { best: number; isNewBest: boolean; record: GameRecord } {
  const record: GameRecord = { id: uuid(), ...game, playedAt: now.toISOString() }
  write(HISTORY_KEY, JSON.stringify([...getHistory(), record].slice(-HISTORY_LIMIT)))
  write(OUTBOX_KEY, JSON.stringify([...getOutbox(), record].slice(-OUTBOX_LIMIT)))

  const previous = getBestScore()
  if (game.score > previous) {
    write(BEST_KEY, String(game.score))
    return { best: game.score, isNewBest: true, record }
  }
  return { best: previous, isNewBest: false, record }
}

/** Партии, которые ещё не дошли до сервера. */
export function getOutbox(): GameRecord[] {
  return readRecords(OUTBOX_KEY)
}

/**
 * Убирает партию из очереди. По id, а не перезаписью всей очереди: пока шла
 * отправка, в неё могла лечь новая партия.
 */
export function removeFromOutbox(id: string): void {
  write(OUTBOX_KEY, JSON.stringify(getOutbox().filter((r) => r.id !== id)))
}

export function getPlayer(): PlayerIdentity | null {
  const p = readJson(PLAYER_KEY)
  if (typeof p !== 'object' || p === null) return null
  const { id, nickname, token } = p as Record<string, unknown>
  return typeof id === 'number' && typeof nickname === 'string' && typeof token === 'string'
    ? { id, nickname, token }
    : null
}

export function setPlayer(player: PlayerIdentity | null): void {
  if (player) write(PLAYER_KEY, JSON.stringify(player))
  else remove(PLAYER_KEY)
}

/**
 * Кошелёк монет — валюта под будущий магазин скинов. Пока только локально:
 * до магазина тратить её не на что, и серверу хранить её незачем.
 */
export function getCoins(): number {
  const value = Number(read(COINS_KEY))
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
}

/** Добавляет монеты партии; возвращает новый баланс. */
export function addCoins(amount: number): number {
  const total = getCoins() + Math.max(0, Math.floor(amount))
  write(COINS_KEY, String(total))
  return total
}

/** Выбранный скин. Проверку, открыт ли он, делает skins.resolveSkin. */
export function getSkinId(): string | null {
  return read(SKIN_KEY)
}

export function setSkinId(id: string): void {
  write(SKIN_KEY, id)
}

export function isMuted(): boolean {
  return read(MUTED_KEY) === '1'
}

export function setMuted(muted: boolean): void {
  write(MUTED_KEY, muted ? '1' : '0')
}

/**
 * uuid v4. crypto.randomUUID есть только в secure context — при открытии игры
 * по http с IP в локальной сети (проверка на телефоне) его нет, а
 * getRandomValues есть везде.
 */
export function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const b = crypto.getRandomValues(new Uint8Array(16))
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function isRecord(value: unknown): value is GameRecord {
  if (typeof value !== 'object' || value === null) return false
  const r = value as Record<string, unknown>
  return (
    typeof r.id === 'string' &&
    typeof r.score === 'number' &&
    typeof r.hits === 'number' &&
    typeof r.duration === 'number' &&
    typeof r.playedAt === 'string'
  )
}
